import React from 'react'
import { FiEdit2, FiTrash2 } from 'react-icons/fi'

const DataTable = ({ title, columns, data, onEdit, onDelete, onAdd }) => {
  if (!data) {
    return (
      <div className="glass-card p-6 text-center">
        <h3 className="text-xl font-semibold text-white mb-4">{title}</h3>
        <p className="text-gray-400">Loading data...</p>
      </div>
    )
  }

  return (
    <div className="glass-card p-4 sm:p-6 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
        <h3 className="text-lg sm:text-xl font-bold font-grotesk text-white">{title}</h3>
        {onAdd && (
          <button 
            onClick={onAdd}
            className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-accent-cyan/20 text-accent-cyan rounded-lg hover:bg-accent-cyan/30 transition text-xs sm:text-sm font-semibold"
          >
            + Add New
          </button>
        )}
      </div>
      
      {data.length === 0 ? (
        <p className="text-gray-400 text-center py-8">No data available.</p>
      ) : (
        <div className="overflow-hidden">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  {columns.map((col, index) => (
                    <th key={index} className="py-3 px-3 lg:px-4 text-xs lg:text-sm font-semibold text-gray-300">
                      {col.header}
                    </th>
                  ))}
                  {(onEdit || onDelete) && (
                    <th className="py-3 px-3 lg:px-4 text-xs lg:text-sm font-semibold text-gray-300 text-right">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                    {columns.map((col, colIndex) => (
                      <td key={colIndex} className="py-3 px-3 lg:px-4 text-xs lg:text-sm text-gray-300 truncate max-w-[150px] lg:max-w-[200px]">
                        {col.render ? col.render(row) : row[col.accessor]}
                      </td>
                    ))}
                    {(onEdit || onDelete) && (
                      <td className="py-3 px-3 lg:px-4 text-sm text-right">
                        <div className="flex justify-end gap-2">
                          {onEdit && (
                            <button onClick={() => onEdit(row)} className="text-accent-cyan hover:text-white transition-colors" title="Edit">
                              <FiEdit2 size={18} />
                            </button>
                          )}
                          {onDelete && (
                            <button onClick={() => onDelete(row)} className="text-red-400 hover:text-red-300 transition-colors" title="Delete">
                              <FiTrash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {data.map((row, rowIndex) => (
              <div key={rowIndex} className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-2">
                {columns.slice(0, Math.min(3, columns.length)).map((col, colIndex) => (
                  <div key={colIndex} className="flex flex-col">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{col.header}</span>
                    <span className="text-sm text-gray-100 break-words">{col.render ? col.render(row) : row[col.accessor]}</span>
                  </div>
                ))}
                {(onEdit || onDelete) && (
                  <div className="flex gap-2 pt-2 border-t border-white/5">
                    {onEdit && (
                      <button onClick={() => onEdit(row)} className="flex-1 py-2 px-3 bg-accent-cyan/20 text-accent-cyan rounded hover:bg-accent-cyan/30 transition text-xs font-semibold flex items-center justify-center gap-2">
                        <FiEdit2 size={16} /> Edit
                      </button>
                    )}
                    {onDelete && (
                      <button onClick={() => onDelete(row)} className="flex-1 py-2 px-3 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition text-xs font-semibold flex items-center justify-center gap-2">
                        <FiTrash2 size={16} /> Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default DataTable
